import StatusMsg from '@/components/common/status-msg';
import { SecBoxLoader } from '@/components/layout/sec-box';
import { ScrollArea } from '@mantine/core';
import axios from 'axios';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

const DailyNetWinChart = ({ className }) => {
    const [dailyIncome, setDailyIncome] = useState();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState({ message: '', field: '' });
    const { remoteId } = useSelector((state) => state.session);

    useEffect(() => {
        const getDailyNetWin = async () => {
            try {
                setLoading(true);
                const params = {
                    action: 'daily-netwin',
                    token: process.env.NEXT_PUBLIC_TOKEN,
                    casino: process.env.NEXT_PUBLIC_CASINO,
                    remote_id: remoteId,
                };
                const response = await axios.get(`${process.env.NEXT_PUBLIC_ADMIN_API_DOMAIN}/casinos/casino-admin-reports`, { params });

                if (response?.data?.data) {
                    const data = response?.data?.data
                        ?.map((e) => {
                            return {
                                ...e,
                                day: moment(e.day).format('DD-MMM'),
                                netwinIncome: typeof e.netwinIncome === 'number' ? Number.parseFloat(e.netwinIncome.toFixed(2)) : 0,
                            };
                        })
                        .reverse();

                    setDailyIncome(data);
                }
            } catch (error) {
                setError({ message: error.message, variant: 'danger' });
            } finally {
                setLoading(false);
            }
        };

        getDailyNetWin();
    }, [remoteId]);

    const data = [
        {
            name: 'Page A',
            uv: 4000,
            pv: 2400,
            amt: 2400,
        },
        {
            name: 'Page B',
            uv: 3000,
            pv: 1398,
            amt: 2210,
        },
        {
            name: 'Page C',
            uv: 2000,
            pv: 9800,
            amt: 2290,
        },
        {
            name: 'Page D',
            uv: 2780,
            pv: 3908,
            amt: 2000,
        },
        {
            name: 'Page E',
            uv: 1890,
            pv: 4800,
            amt: 2181,
        },
        {
            name: 'Page F',
            uv: 2390,
            pv: 3800,
            amt: 2500,
        },
        {
            name: 'Page G',
            uv: 3490,
            pv: 4300,
            amt: 2100,
        },
    ];

    return (
        <div className={clsx(className, 'h-full')}>
            {loading ? (
                <SecBoxLoader />
            ) : error?.message ? (
                <div className="full_screen_msg sec_box_msg">
                    <StatusMsg title={error?.message} variant={error?.variant} />
                </div>
            ) : (
                <ScrollArea type="always" offsetScrollbars scrollbars="x" className={'sec_box_chart_scroll'}>
                    <div style={{ minWidth: '62.5rem', height: '100%' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart
                                data={dailyIncome}
                                margin={{
                                    top: 10,
                                    right: 30,
                                    left: -5,
                                    bottom: 20,
                                }}
                            >
                                <defs>
                                    <linearGradient id="netWinGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="var(--success-color)" stopOpacity={0.5} />
                                        <stop offset="95%" stopColor="var(--success-color)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>

                                <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-color)" />

                                <XAxis
                                    dataKey="day"
                                    interval={0}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{
                                        fontSize: 11,
                                        fill: 'var(--chart-axis-color)',
                                        angle: -45,
                                        dx: -10,
                                        dy: 4,
                                        textAnchor: 'end',
                                    }}
                                />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: 'var(--chart-axis-color)' }} />
                                <Tooltip />

                                <Area
                                    type="monotone"
                                    dataKey="netwinIncome"
                                    stroke="var(--success-color)"
                                    strokeWidth={2}
                                    fill="url(#netWinGradient)"
                                    dot={{
                                        r: 4,
                                        fill: 'var(--success-color)',
                                        stroke: 'var(--success-color)',
                                    }}
                                    activeDot={{
                                        r: 6,
                                        fill: 'var(--success-color)',
                                        stroke: 'var(--success-color)',
                                    }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </ScrollArea>
            )}
        </div>
    );
};

export default DailyNetWinChart;
